import { Controller } from "@hotwired/stimulus"

// Live SMS length/segment counter for the ops drawer. Mirrors Util::Sms
// (lib/util/sms.rb) so the operator sees the same encoded length and segment
// count the server enforces at send time. The draft still carries the
// {{UPLOAD_URL}} placeholder, so we count it as the expanded link length.
export default class extends Controller {
  static targets = ["input", "output"]
  static values = {
    maxSegments: Number,
    single: Number,
    multipart: Number,
    ucs2Single: Number,
    ucs2Multipart: Number,
    placeholder: String,
    urlLength: Number,
  }

  connect() {
    this.update()
  }

  update() {
    const raw = this.inputTarget.value || ""
    const text = this.expandPlaceholder(raw)
    const maxSegments = this.maxSegmentsValue || 2

    const length = this.encodedLength(text)
    const segments = this.segmentCount(text)
    // Encoding-aware cap, mirroring Util::Sms#char_limit: maxSegments worth of the
    // encoding's multipart segment size (GSM-7 306, UCS-2 134).
    const multipart = this.isGsm7(text)
      ? this.multipartValue || 153
      : this.ucs2MultipartValue || 67
    const limit = maxSegments * multipart
    const noun = segments === 1 ? "segment" : "segments"
    this.outputTarget.textContent = `${length} / ${limit} chars · ${segments} ${noun}`

    this.outputTarget.classList.remove(
      "text-support-light-600",
      "text-alert-yellow-700",
      "text-alert-red-700"
    )
    if (segments > maxSegments) {
      this.outputTarget.classList.add("text-alert-red-700")
    } else if (segments > 1) {
      this.outputTarget.classList.add("text-alert-yellow-700")
    } else {
      this.outputTarget.classList.add("text-support-light-600")
    }
  }

  // Swap the {{UPLOAD_URL}} placeholder for a dummy of the real link's length so
  // the count reflects what actually gets sent.
  expandPlaceholder(text) {
    const placeholder = this.placeholderValue || "{{UPLOAD_URL}}"
    if (!placeholder || !text.includes(placeholder)) return text
    const urlLength = this.urlLengthValue || 49
    return text.split(placeholder).join("x".repeat(urlLength))
  }

  isGsm7(text) {
    for (const ch of text) {
      if (!GSM7_BASIC.has(ch) && !GSM7_EXTENSION.has(ch)) return false
    }
    return true
  }

  encodedLength(text) {
    if (this.isGsm7(text)) {
      let len = 0
      for (const ch of text) len += GSM7_EXTENSION.has(ch) ? 2 : 1
      return len
    }
    // UCS-2: count UTF-16 code units (astral chars count as 2).
    return text.length
  }

  segmentCount(text) {
    const length = this.encodedLength(text)
    if (length === 0) return 0
    let single, multipart
    if (this.isGsm7(text)) {
      single = this.singleValue || 160
      multipart = this.multipartValue || 153
    } else {
      single = this.ucs2SingleValue || 70
      multipart = this.ucs2MultipartValue || 67
    }
    return length <= single ? 1 : Math.ceil(length / multipart)
  }
}

// GSM 03.38 default alphabet (one septet each).
const GSM7_BASIC = new Set(
  ("@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?" +
    "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà").split("")
)

// GSM 03.38 extension table (two septets each).
const GSM7_EXTENSION = new Set(["\f", "^", "{", "}", "\\", "[", "~", "]", "|", "€"])
